import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getUsers, getSites, getAssets, getWorkOrders, createAsset, updateAsset } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { TaxonomyPicker } from '../../components/TaxonomyPicker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, MapPin, Boxes, Building2, AlertCircle, Wrench, Calendar, Hash, ClipboardList, Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const CRITICALITY_STYLES = {
  high:   'bg-red-50 text-red-800 border-red-200',
  medium: 'bg-amber-50 text-amber-800 border-amber-200',
  low:    'bg-slate-50 text-slate-700 border-slate-200',
};

const STATUS_STYLES = {
  operational:  'bg-emerald-50 text-emerald-800 border-emerald-200',
  maintenance:  'bg-amber-50 text-amber-800 border-amber-200',
  'down':       'bg-red-50 text-red-800 border-red-200',
};

const CustomerSiteAssets = () => {
  const { customerId, siteId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [assets, setAssets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add-asset dialog (also used for editing — `editing` is the asset
  // doc being edited, or null when creating a fresh asset).
  const [addOpen, setAddOpen] = useState(false);
  const [savingAsset, setSavingAsset] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyAsset = {
    name: '', type: 'Mechanical', status: 'operational', criticality: 'medium',
    location: '', serialNumber: '', notes: '',
  };
  const [assetForm, setAssetForm] = useState(emptyAsset);

  const refresh = () => Promise.all([getAssets(), getWorkOrders()])
    .then(([a, w]) => {
      setAssets(a.filter(x => x.siteId === siteId));
      setOrders(w);
    });

  useEffect(() => {
    Promise.all([getUsers(), getSites(), getAssets(), getWorkOrders()])
      .then(([users, s, a, w]) => {
        setCustomer(users.find(u => u.id === customerId) || null);
        setSite(s.find(x => x.id === siteId) || null);
        setAssets(a.filter(x => x.siteId === siteId));
        setOrders(w);
      })
      .finally(() => setLoading(false));
  }, [customerId, siteId]);

  const openAdd = () => {
    setEditing(null);
    setAssetForm(emptyAsset);
    setAddOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setAssetForm({
      name: a.name || '',
      type: a.type || 'Mechanical',
      status: a.status || 'operational',
      criticality: a.criticality || 'medium',
      location: a.location || '',
      serialNumber: a.serialNumber || '',
      notes: a.notes || '',
    });
    setAddOpen(true);
  };

  const saveAsset = async () => {
    if (!assetForm.name.trim()) { toast.error('Asset name is required'); return; }
    setSavingAsset(true);
    try {
      const payload = {
        ...assetForm,
        name: assetForm.name.trim(),
        location: assetForm.location.trim(),
        serialNumber: assetForm.serialNumber.trim(),
        notes: assetForm.notes.trim(),
      };
      if (editing) {
        // PATCH: customerId / siteId stay locked to the original asset
        // — moving an asset between sites is a separate flow.
        const a = await updateAsset(editing.id, payload);
        toast.success(`Asset "${a.name}" updated`);
      } else {
        // Use the SITE's owning customerId (which may be a sub-customer)
        // rather than the URL param — otherwise assets added on a
        // sub-customer's site would silently attach to the parent and
        // vanish from the sub-customer's own dashboard.
        const owningCustomerId = site?.customerId || customerId;
        const a = await createAsset({ ...payload, customerId: owningCustomerId, siteId });
        toast.success(`Asset "${a.name}" added`);
      }
      setAddOpen(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save asset');
    } finally {
      setSavingAsset(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-slate-500" data-testid="site-assets-loading">Loading...</div>;
  }

  if (!site) {
    return (
      <div className="p-4 sm:p-6 lg:p-8" data-testid="site-not-found">
        <Button variant="outline" onClick={() => navigate(`/admin/customers/${customerId}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="mt-6 bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">Site not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="site-assets-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/customers/${customerId}`)} className="mb-1 -ml-2 text-slate-600">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to {customer?.name || 'customer'}
      </Button>

      <PageHeader
        title={site.name}
        description={
          <>
            <span className="inline-flex items-center gap-1.5 mr-3"><Building2 className="h-3.5 w-3.5" /> {customer?.company || customer?.name}</span>
            {site.address && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {site.address}</span>}
          </>
        }
      />

      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase tracking-wide text-base text-slate-700">Assets at this site</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">
            {assets.length} asset{assets.length === 1 ? '' : 's'}
          </span>
          <Button
            size="sm"
            onClick={openAdd}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="add-asset-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add asset
          </Button>
        </div>
      </div>

      {assets.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <Boxes className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">No assets at this site yet</div>
          <div className="text-sm text-slate-500 mb-4">Add an asset to start scheduling work orders for this location.</div>
          <Button
            onClick={openAdd}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="add-first-asset-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add first asset
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assets.map(a => {
            const openWos = orders.filter(o => o.assetId === a.id && o.status !== 'completed').length;
            return (
              <div
                key={a.id}
                data-testid={`site-asset-${a.id}`}
                className="bg-white border border-slate-200 rounded-md p-5"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{a.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <Hash className="h-3 w-3" /> <span className="font-mono">{a.id}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => openEdit(a)}
                      data-testid={`edit-asset-${a.id}`}
                      className="h-9 w-9 rounded inline-flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 transition-colors"
                      title="Edit asset"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <div className="h-9 w-9 rounded bg-slate-100 flex items-center justify-center">
                      <Wrench className="h-4 w-4 text-slate-600" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {a.type && <Badge variant="outline" className="text-[10px] font-medium">{a.type}</Badge>}
                  {a.status && (
                    <Badge variant="outline" className={`text-[10px] font-medium border ${STATUS_STYLES[a.status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                      {a.status}
                    </Badge>
                  )}
                  {a.criticality && (
                    <Badge variant="outline" className={`text-[10px] font-medium border ${CRITICALITY_STYLES[a.criticality] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                      <AlertCircle className="h-2.5 w-2.5 mr-1" /> {a.criticality}
                    </Badge>
                  )}
                </div>

                {a.location && (
                  <div className="text-xs text-slate-600 flex items-center gap-1.5 mb-2">
                    <MapPin className="h-3 w-3 text-slate-400" /> {a.location}
                  </div>
                )}
                {a.serialNumber && (
                  <div className="text-xs text-slate-500 mb-2">
                    S/N: <span className="font-mono">{a.serialNumber}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100 text-xs">
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <Calendar className="h-3 w-3 text-slate-400" />
                    {a.lastMaintenance ? <>Last: <strong className="text-slate-900">{a.lastMaintenance}</strong></> : 'No service'}
                  </div>
                  <div className="flex items-center gap-1.5 justify-end">
                    {openWos > 0 ? (
                      <Link to="/admin/work-orders" className="text-amber-700 hover:underline inline-flex items-center gap-1">
                        <ClipboardList className="h-3 w-3" /> {openWos} open WO{openWos === 1 ? '' : 's'}
                      </Link>
                    ) : (
                      <span className="text-slate-400">No open WOs</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="add-asset-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : `Add asset to ${site.name}`}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update the asset details. To move the asset to a different site, use the main Assets page.'
                : `Assets at this site are auto-linked to ${customer?.company || customer?.name || 'this customer'}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Asset name</Label>
              <Input
                data-testid="asset-name-input"
                className="mt-1.5"
                placeholder="e.g. Main distribution board"
                value={assetForm.name}
                onChange={e => setAssetForm({ ...assetForm, name: e.target.value })}
                autoFocus
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Type</Label>
                <TaxonomyPicker
                  kind="asset_type"
                  value={assetForm.type}
                  onChange={(v) => setAssetForm({ ...assetForm, type: v || 'Mechanical' })}
                  testId="asset-type-picker"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={assetForm.status} onValueChange={v => setAssetForm({ ...assetForm, status: v })}>
                  <SelectTrigger data-testid="asset-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operational">Operational</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="down">Down</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Criticality</Label>
                <Select value={assetForm.criticality} onValueChange={v => setAssetForm({ ...assetForm, criticality: v })}>
                  <SelectTrigger data-testid="asset-criticality-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Location <span className="text-xs text-slate-500 font-normal">(within site)</span></Label>
                <Input
                  data-testid="asset-location-input"
                  className="mt-1.5"
                  placeholder="e.g. Plant room"
                  value={assetForm.location}
                  onChange={e => setAssetForm({ ...assetForm, location: e.target.value })}
                />
              </div>
              <div>
                <Label>Serial number</Label>
                <Input
                  data-testid="asset-serial-input"
                  className="mt-1.5"
                  placeholder="optional"
                  value={assetForm.serialNumber}
                  onChange={e => setAssetForm({ ...assetForm, serialNumber: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                data-testid="asset-notes-input"
                className="mt-1.5"
                placeholder="Optional installation notes"
                value={assetForm.notes}
                onChange={e => setAssetForm({ ...assetForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={savingAsset}>Cancel</Button>
            <Button
              onClick={saveAsset}
              disabled={savingAsset}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-asset-btn"
            >
              {savingAsset ? 'Saving...' : (editing ? 'Save changes' : 'Save asset')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerSiteAssets;
